import type { SeedRow } from '@radar/contracts';
import { queryOptions } from '@tanstack/react-query';
import type { InferRequestType } from 'hono/client';
import { api, call } from '@/lib/api';
import { qk, type SeedsListFilters } from '@/lib/query-keys';
import type { SeedsSearch } from '@/lib/search-schemas';

export type CreateSeedInput = InferRequestType<typeof api.seeds.$post>['json'];
export type UpdateSeedInput = InferRequestType<(typeof api.seeds)[':id']['$patch']>['json'];
export type ImportSeedsInput = InferRequestType<typeof api.seeds.import.$post>['json'];
export type AnalyzeSiteInput = InferRequestType<
  (typeof api.seeds)['analyze-site']['$post']
>['json'];
export type SeedAssistantInput = InferRequestType<typeof api.seeds.assistant.$post>['json'];
export type KeywordVolumesInput = InferRequestType<
  (typeof api.seeds)['keyword-volumes']['$post']
>['json'];
export type SeedsListData = Awaited<ReturnType<typeof listSeeds>>;

/** Search params de `/seeds` → query string typée de `GET /seeds` (valeurs en chaînes). */
export function toSeedsQuery(search: SeedsSearch, marketId: string | null): SeedsListFilters {
  return {
    page: String(search.page),
    pageSize: String(search.pageSize),
    sort: search.sort,
    order: search.order,
    ...(marketId ? { marketId } : {}),
    ...(search.type && search.type.length > 0 ? { type: search.type } : {}),
    ...(search.category ? { category: search.category } : {}),
    ...(search.isActive === undefined ? {} : { isActive: String(search.isActive) }),
    ...(search.q ? { q: search.q } : {}),
  };
}

/** Compteur « seeds actives » de la sidebar (tous marchés) : une page d'une ligne, seul `total` est lu. */
export const ACTIVE_SEEDS_QUERY: SeedsListFilters = { isActive: 'true', pageSize: '1' };

export function listSeeds(query: SeedsListFilters) {
  return call(api.seeds.$get({ query }));
}

export const seedsListOptions = (query: SeedsListFilters) =>
  queryOptions({ queryKey: qk.seeds.list(query), queryFn: () => listSeeds(query) });

/** Catégories distinctes du marché (page de 200 seeds, dédoublonnée côté client). */
export const seedCategoriesQuery = (marketId: string | null): SeedsListFilters => ({
  pageSize: '200',
  sort: 'label',
  order: 'asc',
  ...(marketId ? { marketId } : {}),
});

export function uniqueCategories(items: Pick<SeedRow, 'category'>[]): string[] {
  const set = new Set<string>();
  for (const item of items) if (item.category) set.add(item.category);
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
}

export const seedCategoriesOptions = (marketId: string | null) =>
  queryOptions({
    ...seedsListOptions(seedCategoriesQuery(marketId)),
    select: (data: SeedsListData) => uniqueCategories(data.items),
    staleTime: 60_000,
  });

export const createSeed = (json: CreateSeedInput) => call(api.seeds.$post({ json }));

export const updateSeed = (id: string, json: UpdateSeedInput) =>
  call(api.seeds[':id'].$patch({ param: { id }, json }));

export const deleteSeed = (id: string) => call(api.seeds[':id'].$delete({ param: { id } }));

export const importSeeds = (json: ImportSeedsInput) => call(api.seeds.import.$post({ json }));

/** `POST /seeds/analyze-site` (T36, T37, payant ≈ 0,10 $) : suggestions avec volumes réels. */
export const analyzeSite = (json: AnalyzeSiteInput) =>
  call(api.seeds['analyze-site'].$post({ json }));

/** `POST /seeds/assistant` (T36, payant ≈ 0,01 $ par message) : un tour de discussion. */
export const assistSeed = (json: SeedAssistantInput) => call(api.seeds.assistant.$post({ json }));

/** `POST /seeds/keyword-volumes` (T37, payant 0,09 $) : volumes réels de 1 à 100 mots-clés. */
export const keywordVolumes = (json: KeywordVolumesInput) =>
  call(api.seeds['keyword-volumes'].$post({ json }));
